import { inject, Injectable } from '@angular/core';
import {
  DateUtilsService,
  InventoryManagementAuctionItemStatus,
  InventoryManagementDateRange,
  InventoryManagementRepostedStatus,
  InventoryManagementSearchCriteria,
  InventoryManagementSortOptions,
  InventoryManagementSortOrder,
  InventoryManagementState,
  LaunchDarklyService,
  StatusFilter
} from 'remarketing-angular-library';
import {
  filterParameters,
  InventoryManagementFilterConfiguration
} from '../interfaces/inventory-management-status-configuration.interface';
import { pick } from 'lodash';
import { SearchFilterSectionService } from '../services/search-filter-section/search-filter-section.service';
import { select } from '@ngxs/store';
import { FilterSectionTypes } from '../enums/inventory-management.enum';

@Injectable({ providedIn: 'root' })
export class SearchFilterFactory {
  private readonly dateUtilsService: DateUtilsService;
  private readonly searchFilterSectionService: SearchFilterSectionService;
  private readonly launchDarklyService: LaunchDarklyService;
  private readonly filterByIsv$ = select(InventoryManagementState.filterByInService);

  constructor() {
    this.dateUtilsService = inject(DateUtilsService);
    this.searchFilterSectionService = inject(SearchFilterSectionService);
    this.launchDarklyService = inject(LaunchDarklyService);
  }

  get isInServiceVehiclesEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('platform-inServiceVehiclesEnabled', false);
  }

  // updateStatus = false when we select a vehicle on the ALL VEHICLES page and filter by that vehicle's auction item status
  buildDefaultFilter(statusFilter: StatusFilter, updateStatus = true) {
    // all attributes not listed are assumed undefined, per the interface

    this.searchFilterSectionService.resetFilterSection();

    const defaultCriteria = new InventoryManagementSearchCriteria({});
    // this is a sub-interface of the search criteria, consisting of fields that are only modified on the left side rail
    const filterCriteria = pick(defaultCriteria, filterParameters) as InventoryManagementFilterConfiguration;

    switch (statusFilter) {
      case StatusFilter.ACTIVE_VEHICLES:
        if (this.filterByIsv$()) {
          this.searchFilterSectionService.setOpenFilterSection(FilterSectionTypes.GROUNDED);
        } else {
          this.searchFilterSectionService.setOpenFilterSection(FilterSectionTypes.OFFERS);
        }
        filterCriteria.filterByStatus = InventoryManagementAuctionItemStatus.ACTIVE;
        break;

      case StatusFilter.SOLD_VEHICLES:
        this.searchFilterSectionService.setOpenFilterSection(FilterSectionTypes.BIDS_OFFERS);
        filterCriteria.filterByStatus = InventoryManagementAuctionItemStatus.SOLD;
        filterCriteria.sortBy = InventoryManagementSortOptions.SOLD_DATE;
        filterCriteria.sortOrder = InventoryManagementSortOrder.DESCENDING;
        filterCriteria.dateRange = {
          fromDate: updateStatus ? this.dateUtilsService.DEFAULT_DATES.today : this.dateUtilsService.DEFAULT_DATES.allVehiclesFromDate,
          toDate: this.dateUtilsService.DEFAULT_DATES.today
        } as InventoryManagementDateRange;
        break;

      case StatusFilter.HOLD_VEHICLES:
        filterCriteria.filterByStatus = InventoryManagementAuctionItemStatus.HOLD;
        filterCriteria.sortBy = InventoryManagementSortOptions.HOLD_DAYS;
        filterCriteria.sortOrder = InventoryManagementSortOrder.DESCENDING;
        break;

      case StatusFilter.REMOVED_VEHICLES:
        filterCriteria.filterByStatus = InventoryManagementAuctionItemStatus.REMOVED;
        filterCriteria.sortBy = InventoryManagementSortOptions.STATUS_CHANGED_DATE;
        filterCriteria.sortOrder = InventoryManagementSortOrder.DESCENDING;
        filterCriteria.filterByReposted = InventoryManagementRepostedStatus.ALL;
        filterCriteria.dateRange = {
          fromDate: this.dateUtilsService.DEFAULT_DATES.removedFromDate,
          toDate: this.dateUtilsService.DEFAULT_DATES.today
        } as InventoryManagementDateRange;
        break;

      case StatusFilter.RESERVED_VEHICLES:
        this.searchFilterSectionService.setOpenFilterSection(FilterSectionTypes.GROUNDED);
        filterCriteria.filterByStatus = InventoryManagementAuctionItemStatus.RESERVED;
        break;

      case StatusFilter.SHARED_VEHICLES:
        this.searchFilterSectionService.setOpenFilterSection(FilterSectionTypes.SHARED_LISTS);
        if (this.isInServiceVehiclesEnabled && this.filterByIsv$()) {
          filterCriteria.filterByInService = true;
        }
        filterCriteria.filterByStatus = null;
        filterCriteria.sortBy = InventoryManagementSortOptions.STATUS_CHANGED_DATE;
        filterCriteria.sortOrder = InventoryManagementSortOrder.DESCENDING;
        filterCriteria.filterByOfferLists = true;
        break;

      case StatusFilter.ALL_VEHICLES:
        filterCriteria.filterByStatus = null;
        filterCriteria.sortBy = InventoryManagementSortOptions.STATUS_CHANGED_DATE;
        filterCriteria.sortOrder = InventoryManagementSortOrder.DESCENDING;
        filterCriteria.dateRange = {
          fromDate: this.dateUtilsService.DEFAULT_DATES.allVehiclesFromDate,
          toDate: this.dateUtilsService.DEFAULT_DATES.today
        };
        break;
    }

    return filterCriteria;
  }
}
